import React, { useState, useMemo, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { saveDomainKey } from "../../actions/hubspotAction";
import { setPageTitle } from "../../shared/utility";
import { ButtonWithLoader } from "../UI/Button/Button";
import CustomToast from "../UI/customToast";
import blitzzHubspotLogo from "../../assets/images/BlitzzHubspot_Bgremove.svg";
import "./HubSpotConfigure.scss";

const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]+$/;

const HubSpotConfigure = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const [domainKey, setDomainKey] = useState("");
    const [portalIdInput, setPortalIdInput] = useState("");
    const [validationMessage, setValidationMessage] = useState("");
    const [portalIdError, setPortalIdError] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [countdown, setCountdown] = useState(10);
    const timerRef = useRef(null);

    useEffect(() => {
        setPageTitle("HubSpot Configure");
    }, []);

    const portalIdFromUrl = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const portalId = params.get("portalId")?.trim() || "";
        // Check for actual valid value (not "null", "undefined", empty, or quoted empty)
        if (portalId && portalId !== "null" && portalId !== "undefined" && portalId !== '""' && portalId !== "''") {
            return portalId;
        }
        return "";
    }, [location.search]);

    useEffect(() => {
        if (!isConnected) return;

        timerRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    window.close();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timerRef.current);
    }, [isConnected]);

    const currentPortalId = portalIdFromUrl || portalIdInput?.trim();

    const isButtonEnabled =
        domainKey.trim().length > 0 &&
        ALPHANUMERIC_REGEX.test(domainKey.trim()) &&
        currentPortalId.length > 0 &&
        !validationMessage &&
        !portalIdError &&
        !isSaving;

    const handleDomainKeyInput = (e) => {
        const value = e.target.value?.toUpperCase() || "";
        setDomainKey(value);

        const trimmedValue = value.trim();
        if (!trimmedValue) {
            setValidationMessage("Domain key is required");
        } else if (!ALPHANUMERIC_REGEX.test(trimmedValue)) {
            setValidationMessage("Only alphanumeric characters allowed (a-z, A-Z, 0-9)");
        } else {
            setValidationMessage("");
        }
    };

    const handlePortalIdInput = (e) => {
        const value = e.target.value || "";
        setPortalIdInput(value);

        if (!value.trim()) {
            setPortalIdError("Portal ID is required");
        } else {
            setPortalIdError("");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && isButtonEnabled) {
            handleSave();
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Ensure uppercase before sending to API
            const upperDomainKey = domainKey?.trim().toUpperCase();
            const payload = {
                HubId: currentPortalId,
                SSOApiKey: upperDomainKey,
            };

            await dispatch(saveDomainKey(payload));

            setIsConnected(true);
        } catch (error) {
            const message = error?.message
                || "Failed to save settings, try again later.";
            CustomToast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="hubspot-configure-page">
            <div className="hubspot-configure-container">
                    <h1 className="visually-hidden">Connect HubSpot to Blitzz</h1>
                    {/* Logo Section */}
                    <div className="hubspot-logos">
                        <img src={blitzzHubspotLogo} alt="Blitzz + HubSpot" />
                    </div>

                    {isConnected ? (
                        <div className="hubspot-success">
                            <div className="success-icon">
                                <span className="icon-check"></span>
                            </div>
                            <h2>Successfully Connected!</h2>
                            <p>Your HubSpot account is now linked to Blitzz.</p>

                            <p className="success-hint">
                                {countdown > 0
                                    ? `Closing automatically in ${countdown}s...`
                                    : "You can safely close this tab now."}
                            </p>
                        </div>
                    ) : (
                        <>
                            <p className="hubspot-description">
                                Connect your HubSpot account to Blitzz
                            </p>
                            <div className="hubspot-form">
                            {!portalIdFromUrl && (
                                <div className="form-field">
                                    <label className="field-label" htmlFor="portalId">
                                        Portal ID
                                    </label>
                                    <input
                                        id="portalId"
                                        className="hubspot-input"
                                        type="text"
                                        placeholder="Enter your HubSpot Portal ID"
                                        value={portalIdInput}
                                        onChange={handlePortalIdInput}
                                        onKeyDown={handleKeyDown}
                                    />
                                    {portalIdError && (
                                        <div className="validation-error">{portalIdError}</div>
                                    )}
                                </div>
                            )}

                            <div className="form-field">
                                <label className="field-label" htmlFor="domainKey">
                                    Blitzz Domain Key
                                </label>
                                <input
                                    id="domainKey"
                                    className="hubspot-input"
                                    type="text"
                                    placeholder="Enter your Blitzz domain key"
                                    value={domainKey}
                                    onChange={handleDomainKeyInput}
                                    onKeyDown={handleKeyDown}
                                />
                                {validationMessage && (
                                    <div className="validation-error">{validationMessage}</div>
                                )}
                            </div>

                            <div className="hubspot-submit">
                                <ButtonWithLoader
                                    name="Connect"
                                    showLoader={isSaving}
                                    disable={!isButtonEnabled}
                                    className="btn btn-primary btn-with-icon hubspot-connect-btn"
                                    onClick={handleSave}
                                />
                            </div>

                            <p className="domain-key-hint">
                                Your Domain Key is provided by Blitzz during onboarding.
                                If you don't have one, contact your Account Executive or support@blitzz.co
                            </p>
                        </div>
                        </>
                    )}
                </div>
        </div>
    );
};

export default HubSpotConfigure;
